// client/src/components/distribution/DownloadPage.js
import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Button, Paper, Accordion,
  AccordionSummary, AccordionDetails, CircularProgress, Alert,
  IconButton, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { Link } from 'react-router-dom';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

export default function DownloadPage() {
  const { featureFlags } = useFeatureFlags();
  const [compose, setCompose] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sourceExists, setSourceExists] = useState(true);

  useEffect(() => {
    axiosInstance.get('/distribution/docker-compose', { responseType: 'text' })
      .then(res => setCompose(res.data))
      .catch(() => setCompose(''))
      .finally(() => setLoading(false));

    // Probe for the source archive
    axiosInstance.head('/distribution/source')
      .catch(err => {
        if (err.response?.status === 404) setSourceExists(false);
      });
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(compose).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!featureFlags.enableDistribution) return null;

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Deploy Your Own InterCollect
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        InterCollect is self-hosted, open-source collection management. Deploy a private instance
        in minutes — no cloud account required. Your data stays on your own infrastructure.
      </Typography>

      {/* docker-compose.yml */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        1. Download the compose file
      </Typography>
      <Paper variant="outlined" sx={{ mb: 3, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton size="small" onClick={handleCopy} disabled={!compose}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : compose ? (
          <Box
            component="pre"
            sx={{
              m: 0, p: 2, overflow: 'auto', maxHeight: 400,
              fontSize: '0.75rem', fontFamily: 'monospace',
              bgcolor: 'background.default', borderRadius: 1,
            }}
          >
            {compose}
          </Box>
        ) : (
          <Alert severity="warning" sx={{ m: 2 }}>
            Could not load the compose file. The distribution may not be configured yet.
          </Alert>
        )}
      </Paper>
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          href="/api/distribution/docker-compose"
          download="docker-compose.yml"
          disabled={!compose}
        >
          Download docker-compose.yml
        </Button>
        {sourceExists && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            href="/api/distribution/source"
            download
          >
            Download source archive
          </Button>
        )}
      </Box>

      {/* Quick start */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        2. Edit credentials and start
      </Typography>
      <Paper variant="outlined" sx={{ mb: 4 }}>
        <Box
          component="pre"
          sx={{ m: 0, p: 2, fontSize: '0.8rem', fontFamily: 'monospace', bgcolor: 'background.default', borderRadius: 1 }}
        >
{`# 1. Open docker-compose.yml and replace:
#      CHANGE_ME_JWT_SECRET      → a long random string
#      CHANGE_ME_MINIO_PASSWORD  → a password (same in all 4 places)

# 2. Start all services
docker compose up -d

# 3. Open http://localhost:3000 and go to /setup to create your admin account`}
        </Box>
      </Paper>

      {/* Updates */}
      <Accordion variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={500}>Updating to a new version</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Each release produces a new compose file with a version tag. Download the new compose file, then run:
          </Typography>
          <Box component="pre" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
{`docker compose pull
docker compose up -d`}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To stay on a specific version, keep the compose file for that version and do not replace it.
            Check release notes for any manual migration steps before upgrading.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* API Keys docs */}
      <Accordion variant="outlined" sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={500}>Setting up external services (API keys)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            InterCollect integrates with external services for game, movie, and music search, barcode
            scanning, and pricing. All are optional — the app runs without any keys, but you'll want
            at least a few for the collection types you use.
          </Typography>
          <Button
            component={Link}
            to="/docs/api-setup"
            variant="outlined"
            size="small"
          >
            View API key setup guide →
          </Button>
        </AccordionDetails>
      </Accordion>

    </Container>
  );
}
